import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  makeStyles,
  Paper,
  InputAdornment,
  TextField,
} from "@material-ui/core";

import { Search, AddBox } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import clsx from "clsx";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import SubscriptionModal from "../../components/SubscriptionModal";
import toastError from "../../errors/toastError";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import SubscriptionsListTable from "../../components/SubscriptionsListTable";
import ContactDrawer from "../../components/ContactDrawer";


const reducer = (state, action) => {
  if (action.type === "LOAD_PLANS") {
    const subscriptions = action.payload;
    const newSubscriptions = [];

    subscriptions.forEach((subscription) => {
      const subscriptionIndex = state.findIndex((q) => q.id === subscription.id);
      if (subscriptionIndex !== -1) {
        state[subscriptionIndex] = subscription;
      } else {
        newSubscriptions.push(subscription);
      }
    });

    return [...state, ...newSubscriptions];
  }

  if (action.type === "UPDATE_PLANS") {
    const subscription = action.payload;
    const subscriptionIndex = state.findIndex((q) => q.id === subscription.id);

    if (subscriptionIndex !== -1) {
      state[subscriptionIndex] = subscription;
      return [...state];
    } else {
      return [subscription, ...state];
    }
  }

  if (action.type === "DELETE_PLANS") {
    const subscriptionId = action.payload;

    const subscriptionIndex = state.findIndex((q) => q.id === subscriptionId);
    if (subscriptionIndex !== -1) {
      state.splice(subscriptionIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    marginLeft: "10px",
    borderRadius: "10px",
  },

  mainWrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    // marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    marginRight: drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // marginRight: 0,
  },
}));

const Subscriptions = () => {
  const classes = useStyles();
  const { settings } = useContext(SettingsContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [subscriptions, dispatch] = useReducer(reducer, []);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState(null);
  const [subscriptionsModalOpen, setSubscriptionModalOpen] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [modulesStreaming, setModulesStreaming] = useState("disabled");
  const [contact, setContact] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const loadsettings = async () => {
      const modulesStreaming = await settings.find(t => t.key === "modulesStreaming");
      setModulesStreaming(modulesStreaming?.value);
    }
    loadsettings();
  }, [settings]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchSubscriptions = async () => {
        try {
          const { data } = await api.get("/subscription/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_PLANS", payload: data.subscriptions });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchSubscriptions();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("subscription", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PLANS", payload: data.subscription });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_PLANS",
          payload: +data.subscriptionId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenSubscriptionModal = () => {
    setSelectedSubscriptions(null);
    setSubscriptionModalOpen(true);
  };

  const handleCloseSubscriptionModal = () => {
    setSelectedSubscriptions(null);
    setSubscriptionModalOpen(false);
  };


  const handleDrawerOpen = (contacts, subscriptionsId) => {
    setContact(contacts)
    setDrawerOpen(true);
    setSelectedSubscriptions(subscriptionsId);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedSubscriptions(0);
    setContact(0)
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div className={classes.root} id="drawer-container">
      <MainContainer>
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          openTicketonSendAlert="false"
        />
        <SubscriptionModal
          open={subscriptionsModalOpen}
          onClose={handleCloseSubscriptionModal}
          aria-labelledby="form-dialog-title"
          subscriptionId={selectedSubscriptions && selectedSubscriptions.id}
          contact={null}
          modules={{ modulesStreaming }}
        ></SubscriptionModal>
        <MainHeader>
          <Title>{i18n.t("subscription.title")}</Title>
          <MainHeaderButtonsWrapper >

            <Hidden mdDown>
              <TextField
                placeholder={i18n.t("global.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Hidden>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSubscriptionModal}
            >
              <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Hidden lgUp>
          <TextField
            placeholder={i18n.t("global.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <br />
        </Hidden>
        <Paper
          variant="outlined"
          onScroll={handleScroll}
          style={{ overflow: 'auto', paddingRight: 20 }}
          className={clsx(`${classes.mainWrapper} ${classes.mainPaper}`, {
            [classes.mainWrapperShift]: drawerOpen,
          })}
        >

          <SubscriptionsListTable
            subscriptions={subscriptions}
            handleDrawerOpen={handleDrawerOpen}
            drawerOpen={drawerOpen}
            modules={{ modulesStreaming }}
          />

          {loading && <TableRowSkeleton columns={4} />}

        </Paper>
      </MainContainer >
    </div>
  );
};

export default Subscriptions;
